// B6 — how a typed gene symbol relates to the registry. `unknown` (registry unreachable) is a
// DISTINCT state from `unresolved` (registry answered: no such symbol) — they lead an author to
// opposite conclusions, and neither ever blocks a save.
import { searchRegistryGenes } from "../../api/geneCodes/RegistryApi";
import { RegistryGene, RegistrySearchOutcome } from "../../types/geneCodes/RegistryTypes";

export interface SymbolMatch {
	gene: RegistryGene;
	/** null = the typed text IS the current symbol; otherwise e.g. "previous symbol PAI1" / "alias PAI-1". */
	via: string | null;
}

const fold = (value: string): string => value.trim().toLowerCase();

// The same derivation the agent client uses: the typed query against symbol, prev_symbols and
// aliases, case-insensitively — never a fuzzy guess of the registry's own ranking.
export const symbolMatchFor = (query: string, gene: RegistryGene): SymbolMatch | null => {
	const q = fold(query);
	if (q === "") return null;
	if (fold(gene.symbol) === q) return { gene, via: null };
	const prev = (gene.prev_symbols ?? []).find((symbol) => fold(symbol) === q);
	if (prev) return { gene, via: `previous symbol ${prev}` };
	const alias = (gene.aliases ?? []).find((symbol) => fold(symbol) === q);
	if (alias) return { gene, via: `alias ${alias}` };
	return null;
};

export type SymbolResolution =
	| { state: "resolved"; gene: RegistryGene }
	| { state: "alias"; candidates: SymbolMatch[] }
	| { state: "unresolved" }
	| { state: "unknown" };

export const resolveSymbol = (query: string, outcome: RegistrySearchOutcome<RegistryGene>): SymbolResolution => {
	if (outcome.status !== "ok") return { state: "unknown" };
	const exact = outcome.results.find((gene) => fold(gene.symbol) === fold(query));
	if (exact) return { state: "resolved", gene: exact };
	const candidates = outcome.results.map((gene) => symbolMatchFor(query, gene)).filter((match): match is SymbolMatch => match !== null);
	if (candidates.length > 0) return { state: "alias", candidates };
	return { state: "unresolved" };
};

const cache = new Map<string, SymbolResolution>();

/** Cached per symbol. `unknown` is deliberately NEVER cached — the next look must retry the registry. */
export const resolveSymbolInRegistry = async (symbol: string): Promise<SymbolResolution> => {
	const key = fold(symbol);
	const hit = cache.get(key);
	if (hit) return hit;
	const resolution = resolveSymbol(symbol, await searchRegistryGenes(symbol, 10));
	if (resolution.state !== "unknown") cache.set(key, resolution);
	return resolution;
};

export const __resetSymbolResolutionCache = (): void => cache.clear();
